import type { OfflineSnapshot } from "@observe-tech/utils";
import {
  isSnapshotUsable,
  listOfflineUsers,
  readSnapshot,
} from "./offlineSession";

export type BootDecision = "online" | "offline" | "login";

export interface BootstrapDeps {
  // Resolves the id token of a live session, or null when there is a session
  // shaped hole rather than an error. Throws whatever amplify throws.
  fetchIdToken(): Promise<string | null>;
  bridgeToken(token: string): void;
  loadInitialData(): Promise<void>;
  hydrateFromSnapshot(snapshot: OfflineSnapshot): void;
  offlineModeEnabled(): boolean;
  probeOnline(): Promise<boolean>;
  readSnapshot(): Promise<OfflineSnapshot | null>;
  // Sign-out, token cleanup and the redirect. Only a real auth error gets here:
  // a network failure must never end a session.
  onAuthFailure(error: unknown): void | Promise<void>;
  now?(): number;
}

export function isAuthError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name ?? "";
  const msg = (err as { message?: string } | null)?.message ?? "";
  if (name === "NotAuthorizedException") return true;
  if (/refresh\s*token/i.test(msg)) return true;
  if (/not\s*authorized/i.test(msg)) return true;
  return false;
}

// Which enrolment is resuming is a question only the offline login screen can
// ask the operator, so an unattended boot answers it only when there is one
// possible answer. A shared station with several enrolments lands on /login.
export async function loadBootSnapshot(): Promise<OfflineSnapshot | null> {
  const users = await listOfflineUsers();
  if (users.length !== 1) return null;
  return readSnapshot(users[0].cognitoSub);
}

// Offline is a claim about the network, so it is only ever reached from a
// failed session: probing costs a round trip and the flag check costs nothing,
// which is the order they run in.
async function canBootOffline(
  deps: BootstrapDeps,
): Promise<OfflineSnapshot | null> {
  if (!deps.offlineModeEnabled()) return null;
  if (await deps.probeOnline()) return null;
  const snapshot = await deps.readSnapshot();
  if (!snapshot) return null;
  const now = deps.now?.() ?? Date.now();
  return isSnapshotUsable(snapshot, now) ? snapshot : null;
}

// The whole boot decision, lifted out of main.tsx so it can be exercised
// without a DOM boot. main.tsx keeps the wiring and nothing else.
export async function bootstrapSession(
  deps: BootstrapDeps,
): Promise<BootDecision> {
  try {
    const token = await deps.fetchIdToken();
    if (token) {
      deps.bridgeToken(token);
      await deps.loadInitialData();
      return "online";
    }
    // No token and no error is cognito answering and refusing: getTokens
    // returns null on NotAuthorizedException and throws on a network or
    // service error, so this is the same dead session the thrown
    // NotAuthorizedException is, in the shape amplify actually returns it.
    // Tearing it down the same way, or the boot ends on a shell with no MFE
    // mounted and no redirect away from it. Not an offline candidate for the
    // same reason: null means the cloud was reached.
    await deps.onAuthFailure(null);
    return "login";
  } catch (error) {
    console.warn("[shell] bootstrap session failed:", error);
    if (isAuthError(error)) {
      await deps.onAuthFailure(error);
      return "login";
    }
    const snapshot = await canBootOffline(deps);
    if (snapshot) {
      // No bridgeToken: the id token behind this snapshot is dead, and writing
      // it to localStorage makes every MFE guard fail in a way that reads as a
      // permissions bug rather than as an offline session.
      deps.hydrateFromSnapshot(snapshot);
      return "offline";
    }
  }
  return "login";
}

export interface OfflineExpiryDeps {
  clearMfeTokens(): void;
  clearSnapshot(cognitoSub: string): Promise<void>;
  captureReturnTo(): void;
  navigate(url: string): void;
}

// The one place an enrolment is dropped without the user asking for it. It has
// run out of time, so the login screen must not go on offering it — PR 7's
// user list filters on the same expiry as a second guard.
export async function handleOfflineExpiry(
  cognitoSub: string | null,
  deps: OfflineExpiryDeps,
): Promise<void> {
  deps.clearMfeTokens();
  if (cognitoSub) {
    await deps.clearSnapshot(cognitoSub);
  }
  // Running out of time is not a reason to lose the page: the same teardown
  // the online session gets. captureReturnTo screens shell-owned paths itself,
  // so it needs no guard here.
  deps.captureReturnTo();
  deps.navigate("/login");
}
